import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LANGUAGES } from "@/lib/languages";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail } from "lucide-react";
import { FindoSpinner } from "@/components/FindoSpinner";

/** Superadmin multilingual email preview — render any transactional email in any
 * of the 32 selector languages with sample data, before it goes out to recipients. */
export default function EmailPreviewPanel() {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState("");
  const [lang, setLang] = useState("fr");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load(attempt = 0) {
      try {
        const r = await api("/admin/email-preview/templates");
        if (cancelled) return;
        setTemplates(r.templates || []);
        if (r.templates?.length) setTemplate(r.templates[0].key);
      } catch (e) {
        if (cancelled) return;
        if (attempt < 3) { setTimeout(() => load(attempt + 1), 1200); return; }
        toast.error(e.message);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!template) return;
    setLoading(true);
    api(`/admin/email-preview?template=${encodeURIComponent(template)}&lang=${encodeURIComponent(lang)}`)
      .then(setPreview)
      .catch((e) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [template, lang]);

  const isRtl = ["ar", "he"].includes(lang);

  return (
    <div className="bg-white rounded-2xl border border-border card-shadow p-6" data-testid="email-preview-panel">
      <div className="flex items-center gap-2.5 mb-1">
        <div className="h-9 w-9 grid place-items-center rounded-xl bg-findo-indigo/10 text-findo-indigo"><Mail size={18} /></div>
        <h3 className="font-display font-bold text-lg text-slate-900">Aperçu des e-mails (multilingue)</h3>
      </div>
      <p className="text-sm text-slate-500 mb-5">Prévisualisez n'importe quel e-mail transactionnel dans l'une des 32 langues, avec des données d'exemple — exactement tel que le destinataire le recevra.</p>

      <div className="grid sm:grid-cols-2 gap-3 mb-5">
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">E-mail</label>
          <Select value={template} onValueChange={setTemplate}>
            <SelectTrigger className="mt-1 h-11 rounded-full" data-testid="email-preview-template-select">
              <SelectValue placeholder="Choisir un e-mail" />
            </SelectTrigger>
            <SelectContent>
              {templates.length === 0 ? (
                <div className="px-3 py-2 text-sm text-slate-400" data-testid="email-template-empty"><span className="inline-flex items-center gap-1.5"><FindoSpinner size={16} />Chargement des e-mails…</span></div>
              ) : templates.map((t) => (
                <SelectItem key={t.key} value={t.key} data-testid={`email-template-${t.key}`}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Langue</label>
          <Select value={lang} onValueChange={setLang}>
            <SelectTrigger className="mt-1 h-11 rounded-full" data-testid="email-preview-lang-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {LANGUAGES.map((l) => (
                <SelectItem key={l.code} value={l.code} data-testid={`email-lang-${l.code}`}>
                  <span className="flex items-center gap-2"><span className="text-base">{l.flag}</span> {l.name}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="h-96 rounded-xl border border-border bg-slate-50 grid place-items-center text-slate-400" data-testid="email-preview-loading">
          <FindoSpinner size={26} />
        </div>
      ) : preview ? (
        <div>
          <div className="rounded-xl bg-slate-50 border border-border px-4 py-2.5 mb-3">
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Objet</span>
            <p className="font-semibold text-slate-900 mt-0.5" dir={isRtl ? "rtl" : "ltr"} data-testid="email-preview-subject">{preview.subject}</p>
          </div>
          <iframe
            title="email-preview"
            className="w-full h-[520px] rounded-xl border border-border bg-white"
            srcDoc={preview.html}
            data-testid="email-preview-frame"
          />
        </div>
      ) : (
        <div className="h-40 grid place-items-center text-slate-400 text-sm">Sélectionnez un e-mail.</div>
      )}
    </div>
  );
}
